/* File: frontend/src/components/dashboard/ClientDocumentsWidget.js */
/* Version: 5.0 (Platinum Polish - Visual Consistency) */
/* Date: 2025-12-10 13:10:00 EST */
/* Description: 
 * - UI: Aligned with Platinum Design System (Shadows, Fonts, Hover effects).
 * - FIX: Removed 'marginTop' which broke the Dashboard Grid alignment.
 * - UX: Added interactive hover states for rows and download buttons.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FileText, Download, File, ArrowRight } from 'lucide-react';

// ── ПАЛИТРА ЭКРАНА «СЕГОДНЯ» → §3.1 (Ф1, quick-260909-jl8) ─────────────────
// Откат в каждой строке — ПРЕЖНИЙ литерал ЭТОГО ЖЕ места. На fitenko токен
// объявлен (styles/fitenko-tokens.css, селектор html.fitenko) и берётся он;
// на MLG и ELN токена НЕТ — берётся откат, то есть РОВНО прежний цвет.
// 🔴 Значения отката менять нельзя: ими доказывается неизменность чужих арендаторов.
// 🔴 Голый var(--ink) запрещён: у MLG он разрешается в ПУСТОТУ (отсутствие цвета),
//    а не в другой цвет — объявление становится недействительным.
// Класс C (подложки чипов) — по решению владельца от 2026-09-09, вариант B:
// подложка выводится правилом color-mix(in srgb, АКЦЕНТ 14%, --surface); правило
// объявлено ОДИН раз в pages/Dashboard.css под селектором html.fitenko. Бирюза —
// вариант T1: своего тона в §3.1 нет, берётся --green-accent.
const P = {
    canvas:      'var(--canvas, #f8fafc)',  // класс A
    ink:         'var(--ink, #1e293b)',  // класс A
    line:        'var(--line, #e2e8f0)',  // класс A
    lineStrong:  'var(--line, #cbd5e1)',  // класс A
    muted:       'var(--ink-muted, #64748b)',  // класс A
    mutedSoft:   'var(--ink-muted, #94a3b8)',  // класс A
    surfaceSoft: 'var(--surface-soft, #f1f5f9)',  // класс A
    blue1:       'var(--blue-accent, #3b82f6)',  // класс B
    blue2:       'var(--blue-accent, #2563eb)',  // класс B
    chipBlue1:   'var(--chip-blue-bg, #eff6ff)',  // класс C
};




const ClientDocumentsWidget = ({ documents }) => {
    // Common Card Styles (Platinum Standard)
    const cardStyle = {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: `1px solid ${P.surfaceSoft}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };

    // Empty State
    if (!documents || documents.length === 0) {
        return (
            <div className="card" style={cardStyle}>
                <div className="card-header" style={{ padding: '24px', borderBottom: `1px solid ${P.canvas}` }}>
                    <h3 className="card-title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: P.ink, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FileText size={20} style={{ stroke: P.blue1 }}/> Recent Documents
                    </h3>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: P.mutedSoft, gap: '12px' }}>
                    <div style={{ background: P.chipBlue1, padding: '16px', borderRadius: '50%' }}>
                        <File size={32} opacity={0.5} style={{ stroke: P.blue1 }} />
                    </div>
                    <span>No documents found.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="card" style={cardStyle}>
            {/* Inject Custom Styles for Hovers */}
            <style>{`
                .doc-item { transition: all 0.2s ease; border-left: 3px solid transparent; }
                .doc-item:hover { background-color: ${P.canvas}; border-left: 3px solid ${P.blue1}; }
                
                .download-btn { 
                    opacity: 0.7; transition: all 0.2s; 
                    background: white; border: 1px solid ${P.line};
                    color: ${P.muted}; border-radius: 8px; padding: 8px;
                    cursor: pointer; display: flex; align-items: center; justify-content: center;
                }
                .doc-item:hover .download-btn { opacity: 1; border-color: ${P.lineStrong}; color: ${P.blue1}; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
                .download-btn:hover { background: ${P.chipBlue1} !important; transform: scale(1.05); }
                
                .view-all-link:hover { text-decoration: underline; color: ${P.blue2} !important; }
            `}</style>

            {/* HEADER */}
            <div className="card-header" style={{
                padding: '20px 24px',
                borderBottom: `1px solid ${P.canvas}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h3 className="card-title" style={{
                    margin: 0, fontSize: '1.1rem', fontWeight: '700', color: P.ink,
                    display: 'flex', alignItems: 'center', gap: '10px'
                }}>
                    <FileText size={20} style={{ stroke: P.blue1 }}/> Recent Documents
                </h3>
                <Link to="/documents" className="view-all-link" style={{
                    fontSize: '0.8rem', color: P.muted, textDecoration: 'none', 
                    fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px'
                }}>
                    View All <ArrowRight size={14}/>
                </Link>
            </div>

            {/* LIST */}
            <div className="task-list" style={{ padding: '0', overflowY: 'auto', flex: 1 }}>
                {documents.map((doc, idx) => (
                    <div key={doc.id || idx} className="doc-item" style={{
                        padding: '16px 24px',
                        borderBottom: `1px solid ${P.surfaceSoft}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px'
                    }}>
                        {/* Icon + Text */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div className="task-icon" style={{
                                width: '40px', height: '40px', borderRadius: '10px',
                                background: P.chipBlue1, color: P.blue1,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <File size={20} />
                            </div>
                            <div className="task-text" style={{ display: 'flex', flexDirection: 'column' }}>
                                <h4 style={{ margin: '0 0 2px 0', fontSize: '0.95rem', fontWeight: '600', color: P.ink }}>
                                    {doc.name || doc.title}
                                </h4>
                                <p style={{ margin: 0, fontSize: '0.8rem', color: P.muted }}>
                                    {doc.date} • {doc.size || 'PDF'}
                                </p>
                            </div>
                        </div>
                        
                        {/* Download Button */}
                        <button className="download-btn" title="Download">
                            <Download size={18}/>
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

ClientDocumentsWidget.propTypes = { documents: PropTypes.array };
export default ClientDocumentsWidget;